import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {DefaultValues, PACKAGE_NAMES, resolveImageScale} from '@maporium-workspace/shared';
import {GraphMap, GraphNode} from '../../api/interfaces/graph-node.interface';
import {PackageService} from '../../api/services/package.service';
import {CommonModule} from '@angular/common';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MetaDataControlComponent} from '../../shared/forms/key-value-table/meta-data-control.component';
import {MatListModule} from '@angular/material/list';
import {SelectInputComponent} from '../../shared/forms/select-input/select-input.component';
import {NodeStyleComponent} from './sections/node-style.component';
import {MatButtonModule} from '@angular/material/button';
import {isEmpty, isEqual, isUndefined, omit, orderBy, pick} from 'lodash';
import {safeDeepClone} from '../../services/cytoscape-safe-clone.util';
import {systemMetadata, visibleMetadata} from '../../shared/helpers/system-metadata.helpers';
import {BOTTLENECK_KEY, BottleneckMarkerComponent} from '../bottleneck-marker/bottleneck-marker.component';
import {MetaData} from '../../api/interfaces/metadata';
import {SidebarFormAbstractComponent} from '../abstract/sidebar-form-abstract.component';
import {
  TitleDescriptionInputComponent
} from '../../shared/forms/title-description-input/title-description-input.component';
import {CdkAccordion} from '@angular/cdk/accordion';
import {
  MaporiumAccordionItemComponent
} from '../../shared/components/maporium-accordion/maporium-accordion-item.component';
import {metadata} from 'reflect-metadata/no-conflict';
import {CommentsComponent} from '../../shared/components/comments/comments.component';
import {
  hasAddedNewProperty,
  hasGeneratedNewMetadata,
  lastMultiSelectionMetadataIds,
  undoRedoJustHappened,
  updateInProgress,
} from '../../shared/helpers/signal.helpers';
import {TranslatePipe} from '@ngx-translate/core';
import {HasPackageDirective} from '../../packages/has-package.directive';
import {HasMapPermissionDirective} from '../../packages/has-map-permission.directive';
import {DomUtils} from '../../shared/helpers/dom.utils';
import {debounceTime, distinctUntilChanged, filter, Subscription} from 'rxjs';
import {undoRedoStateChanged$} from '../../services/undo-redo/undo-redo-manager.service';
import {diff} from '../../shared/helpers/diff';
import {MapService} from '../../api/services/map.service';
import {isInIframe} from '../../shared/helpers/maporium.validators';
import {CytoscapeService} from '../../services/cy-services/cytoscape.service';
import {DashboardComponent} from '../../dashboard/dashboard.component';
import {TourMatMenuModule} from 'ngx-ui-tour-md-menu';
import {CommandFactoryService} from '../../services/undo-redo/command-factory.service';
import {FocusSessionService} from '../../services/undo-redo/focus-session.service';
import {UndoRedoManagerService} from '../../services/undo-redo/undo-redo-manager.service';

@Component({
  selector: 'maporium-node-form',
  templateUrl: './node-form.component.html',
  styleUrls: ['./node-form.component.scss'],
  standalone: true,
  imports: [
    NodeStyleComponent,
    ReactiveFormsModule,
    CommonModule,
    MetaDataControlComponent,
    MatListModule,
    SelectInputComponent,
    MatButtonModule,
    TitleDescriptionInputComponent,
    CdkAccordion,
    MaporiumAccordionItemComponent,
    CommentsComponent,
    TranslatePipe,
    HasPackageDirective,
    HasMapPermissionDirective,
    TourMatMenuModule,
    BottleneckMarkerComponent,
  ],
})
export class NodeFormComponent
  extends SidebarFormAbstractComponent<ExtendedNode>
  implements OnInit, OnDestroy, OnChanges
{
  formGroup = new FormGroup({
    name: new FormControl(''),
    description: new FormControl(''),
    properties: new FormGroup({
      label: new FormControl(DefaultValues.node.properties.label),
      textValign: new FormControl(
        DefaultValues.node.properties.textValign.toString(),
      ),
      textHalign: new FormControl(
        DefaultValues.node.properties.textHalign.toString(),
      ),
      textMarginX: new FormControl(
        DefaultValues.node.properties.textMarginX.toString(),
      ),
      textRotation: new FormControl(
        DefaultValues.node.properties.textRotation.toString(),
      ),
      imageOpacity: new FormControl(DefaultValues.node.properties.imageOpacity),
      imageScale: new FormControl(DefaultValues.node.properties.imageScale),
      opacity: new FormControl(DefaultValues.node.properties.opacity),
      colorOpacity: new FormControl(DefaultValues.node.properties.colorOpacity),
      textMarginY: new FormControl(
        DefaultValues.node.properties.textMarginY.toString(),
      ),
      textFontSize: new FormControl(
        DefaultValues.node.properties.textFontSize.toString(),
      ),
      textFontColor: new FormControl(
        DefaultValues.node.properties.textFontColor.toString(),
      ),
      scale: new FormControl(DefaultValues.node.properties.scale.toString()),
      borderStyle: new FormControl(DefaultValues.node.properties.borderStyle),
      soundUrl: new FormControl(DefaultValues.node.properties.soundUrl),
    }),
    color: new FormControl(DefaultValues.node.color),
    imageUrl: new FormControl(''),
    weight: new FormControl(DefaultValues.node.weight.toString()),
    type: new FormControl(DefaultValues.node.type),
    excludedFromState: new FormControl('false'),
    metadata: new FormControl([]),
  });
  commentsCount = 0;

  @Input() isDefaultState = false;
  @Input() excludeFromStateOptions: {
    key: string;
    value: string;
    icon?: string;
  }[] = [];
  @Input() focusedNode: ExtendedNode | undefined;
  @Output() resetEvent = new EventEmitter<{ node: ExtendedNode }>();
  @Output() updateNodeEvent = new EventEmitter<Partial<ExtendedNode>>();
  @Output() metaExtend = new EventEmitter<void>();
  @Output() styleTouched = new EventEmitter<{ id?: string; path: string; value: unknown }>();
  @ViewChild('nodeNameInput') nodeNameInput!: ElementRef;
  public sectionsCount = 0;

  public currentMap: GraphMap;
  protected readonly isInIframe = isInIframe;

  private undoRedoSubscription?: Subscription;
  private styleTouchSubscription?: Subscription;
  private lastSavedSnapshot: Record<string, any> = {};

  override init() {
    this.formGroup.valueChanges
      .pipe(
        filter(() => !this.isPatchingForm),
        debounceTime(this.debounceTime),
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
      )
      .subscribe((form) => {
        const b = {
          ...form,
          properties: {
            ...form.properties,
            scale: parseFloat(form?.properties?.scale as string),
          },
        };
        // Compare against the saved snapshot, NOT the live focusedNode reference.
        // focusedNode is a reference to Cytoscape's data which gets mutated by
        // onInstantNameChange on every keystroke, making diff() always return empty.
        const toCompare = pick(this.lastSavedSnapshot, Object.keys(b));
        const c = diff(
          omit(toCompare, 'excludedFromState'),
          omit(b, 'excludedFromState'),
        );

        // Skip update if we just patched the form to avoid false diffs from state transitions
        if (!isEmpty(c) && !this.isPatchingForm) {
          this.update();
        }
      });

    // Subscribe to undo/redo events to force-sync the form with current cytoscape state
    this.undoRedoSubscription = undoRedoStateChanged$.subscribe(() => {
      // Only sync when actual undo/redo happened, not when new commands are pushed
      if (!undoRedoJustHappened()) return;

      const nodeId = this.focusedNode?.id;
      if (nodeId && this.cytoscapeService.cy) {
        const cyNode = this.cytoscapeService.cy.$('#' + nodeId);
        if (cyNode && cyNode.length) {
          const nodeData = cyNode.data();
          // Update focusedNode with current cytoscape data and patch the form
          const updatedNode = {...this.focusedNode!, ...nodeData};
          this.focusedNode = updatedNode;
          // Reset hasAddedNewProperty to allow patchForm to update metadata
          // This ensures undo/redo properly refreshes the metadata form
          hasAddedNewProperty.set(false);
          this.patchForm(updatedNode);
          // Force change detection so child components (like TitleDescriptionInput) see the update
          this.cdr.detectChanges();
        }
      }
    });
  }

  ngOnDestroy(): void {
    // Unsubscribe from undo/redo events
    this.undoRedoSubscription?.unsubscribe();
    this.styleTouchSubscription?.unsubscribe();

    // Skip update if switching to link selection (e.g., after link reconnection)
    // This prevents triggering node updates when focus is moving to a link
    if (DashboardComponent.tappedLink) {
      return;
    }

    // Skip update if undo/redo just happened to prevent re-applying undone changes
    if (undoRedoJustHappened()) {
      return;
    }

    // Skip update if this node was part of a recent multi-selection metadata change
    // The multi-selection already created its own command, no need for a duplicate singular one
    if (this.focusedNode?.id && lastMultiSelectionMetadataIds().has(this.focusedNode.id)) {
      return;
    }

    // Always update if we have a focused node, regardless of current selection state.
    // This ensures changes (like newly added properties) are saved even when
    // deselecting by clicking the canvas, before the debounced form update fires.
    if (this.focusedNode?.id) {
      this.update();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const previousNode = changes['focusedNode']?.previousValue as ExtendedNode;
    const currentNode = changes['focusedNode']?.currentValue as ExtendedNode ?? this.focusedNode;

    // Check if undo/redo just happened - always force patch in this case
    const forceUndoRedoSync = undoRedoJustHappened();

    // If undo/redo happened, force sync the form with current focusedNode state
    // This handles cases where Angular might not detect a change to focusedNode
    if (forceUndoRedoSync && this.focusedNode) {
      this.patchForm(this.focusedNode);
      // Note: Signal reset is handled by undo-redo-manager after a delay
      return;
    }

    // Check if object reference changed (new object from undo/redo)
    // We compare against actual form values since focusedNode gets mutated locally
    const formName = this.formGroup.get('name')?.value;
    const formDescription = this.formGroup.get('description')?.value;
    const formColor = this.formGroup.get('color')?.value;
    const propsChanged = currentNode && previousNode?.id === currentNode.id &&
        (formName !== currentNode.name ||
            formDescription !== currentNode.description ||
            formColor !== currentNode.color);

    // Also check if it's a different object reference with same ID (undo/redo creates new objects)
    const isNewReference = previousNode !== currentNode &&
        previousNode?.id === currentNode?.id;

    const earlyReturn =
      updateInProgress() &&
      !isUndefined(previousNode) &&
      previousNode.id === currentNode.id &&
      !hasGeneratedNewMetadata() &&
      !propsChanged &&
      !isNewReference;

    // Skip update if user is actively editing, unless properties changed externally (undo/redo)
    if (earlyReturn) {
      return;
    }

    // we should update the form if the focusedNode changes
    if (changes['focusedNode'] && changes['focusedNode'].currentValue) {
      const node = changes['focusedNode'].currentValue as ExtendedNode;

      // Patch form when:
      // 1. Node ID changes (different node selected)
      // 2. hasGeneratedNewMetadata is true
      // 3. Same node but properties changed (e.g., from undo/redo)
      // 4. New object reference with same ID (undo/redo)
      const idChanged = changes['focusedNode'].previousValue?.id !== node.id;

      if (idChanged || hasGeneratedNewMetadata() || propsChanged || isNewReference) {
        // MAP-1543: don't re-patch (resetting imageUrl/soundUrl/name and jumping
        // the caret) while the user is actively editing this same node. Still
        // patch on a different node, generated metadata, or undo/redo.
        const isEditingThisNode = !idChanged
          && !forceUndoRedoSync
          && !hasGeneratedNewMetadata()
          && this.el.nativeElement.contains(document.activeElement);
        if (!isEditingThisNode) {
          if (idChanged) {
            hasAddedNewProperty.set(false);
          }
          this.patchForm(node);
          hasGeneratedNewMetadata.set(false);
        }
      }
      if (this.focusedNode?.isNew) {
        this.selectNameInput();
      }
    }

    // check if the new value of node has isNew property and if it does, we should select the name input
    if (changes['focusedNode'] && changes['focusedNode'].currentValue?.isNew) {
      if (this.focusedNode?.isNew) {
        this.selectNameInput();
      }
    }
    this.sectionsCount = DomUtils.getElementCount('nodeStyleSection');
  }

  ngOnInit(): void {
    this.init();
    this.styleTouchSubscription = this.watchStyleTouches((path, value) =>
      this.styleTouched.emit({id: this.focusedNode?.id, path, value}),
    );
    setTimeout(() => {
      this.updateSectionCount();
      this.cdr.detectChanges();
    }, 300);
  }

  updateSectionCount() {
    setTimeout(() => {
      this.sectionsCount = DomUtils.getElementCount('nodeStyleSection');
    }, 300);
  }

  selectNameInput() {
    setTimeout(() => {
      this.nodeNameInput?.nativeElement?.select();
    }, 200);
  }

  resetState() {
    if (this.focusedNode) {
      this.resetEvent.emit({ node: this.focusedNode });
    }
  }

  patchForm(node: ExtendedNode) {
    this.isPatchingForm = true;
    this.formGroup.patchValue(
      {
        name: node.name,
        description: node.description,
        color: node.color,
        imageUrl: node.imageUrl,
        weight: String(node.weight),
        type: node.type,
        excludedFromState: node.excludedFromState === true ? 'true' : 'false',
        properties: {
          label: this.getValueOrDefault(
            node.properties?.label,
            this.DefaultValues.node.properties.label,
          ),
          textValign: this.getValueOrDefault(
            node.properties?.textValign,
            this.DefaultValues.node.properties.textValign,
          ),
          textHalign: this.getValueOrDefault(
            node.properties?.textHalign,
            this.DefaultValues.node.properties.textHalign,
          ),
          textMarginX: this.getValueOrDefault(
            node.properties?.textMarginX,
            this.DefaultValues.node.properties.textMarginX,
          ),
          textRotation: this.getValueOrDefault(
            node.properties?.textRotation,
            this.DefaultValues.node.properties.textRotation,
          ),
          imageOpacity: this.getValueOrDefault(
            node.properties?.imageOpacity,
            this.DefaultValues.node.properties.imageOpacity,
          ),
          imageScale: this.getValueOrDefault(
            node.properties?.imageScale,
            this.DefaultValues.node.properties.imageScale,
          ),
          opacity: this.getValueOrDefault(
            node.properties?.opacity,
            this.DefaultValues.node.properties.opacity,
          ),
          colorOpacity: this.getValueOrDefault(
            node.properties?.colorOpacity,
            this.DefaultValues.node.properties.colorOpacity,
          ),
          textMarginY: this.getValueOrDefault(
            node.properties?.textMarginY,
            this.DefaultValues.node.properties.textMarginY,
          ),
          textFontSize: this.getValueOrDefault(
            node.properties?.textFontSize,
            this.DefaultValues.node.properties.textFontSize,
          ),
          textFontColor: this.getValueOrDefault(
            node.properties?.textFontColor,
            this.DefaultValues.node.properties.textFontColor,
          ),
          scale: this.getScale(node.properties?.scale).toString(),
          borderStyle: this.getValueOrDefault(
            node.properties?.borderStyle,
            this.DefaultValues.node.properties.borderStyle,
          ),
          soundUrl: this.getValueOrDefault(
            node.properties?.soundUrl,
            this.DefaultValues.node.properties.soundUrl,
          ),
        },
      },
      { emitEvent: false },
    );

    // Handle metadata updates without emitting change events
    // Skip if user just added a new property row - prevents focus loss
    if (!hasAddedNewProperty()) {
      if (node.metadata !== undefined) {
        this.metadataControl.setValue(
            orderBy(visibleMetadata(node.metadata), ['order'], ['asc']),
            {emitEvent: false},
        );
      } else {
        this.metadataControl.setValue([], {emitEvent: false});
      }
    }
    // Snapshot the node state for diff comparison — must be a deep copy
    // so it's not affected by Cytoscape data mutations from onInstantNameChange.
    // safeDeepClone strips collapsedChildren which is a live cytoscape Collection
    // and would otherwise throw a circular-structure error (MAP-1435).
    this.lastSavedSnapshot = safeDeepClone(node);
    // The form only ever holds non-system metadata, so the snapshot must match
    // or the diff would flag system entries as unsaved changes forever.
    this.lastSavedSnapshot['metadata'] = orderBy(visibleMetadata(this.lastSavedSnapshot['metadata']), ['order'], ['asc']);

    // Re-enable change events after debounce period to avoid false triggers
    setTimeout(() => {
      this.isPatchingForm = false;
      // Check if the user typed while patching was blocked — if so, trigger a save
      this.checkForUnsavedChanges();
    }, this.debounceTime + 100);
  }

  private nameSaveTimer: any;

  /**
   * Name field gained focus: start (or resume) a focus session so the first
   * edit made from here on is pushed as its own undo entry, capturing the
   * value the field had *before* any of this session's edits.
   */
  onNameFieldFocus() {
    if (!this.focusedNode?.id) return;
    const currentValue = this.cytoscapeService.cy
      ?.getElementById(this.focusedNode.id)
      ?.data('name') ?? this.focusedNode.name ?? '';
    this.focusSessionService.startSession(this.focusedNode.id, 'node', 'name', currentValue);
  }

  onInstantNameChange(name: string) {
    if (!this.focusedNode?.id || !this.cytoscapeService.cy) return;
    const cyElement = this.cytoscapeService.cy.getElementById(this.focusedNode.id);
    if (!cyElement || !cyElement.length) return;
    cyElement.data('name', name);
    this.cytoscapeService.cy.style().update();

    // A brand-new node that has never lost focus yet: naming it is part of
    // the creation action itself, not a separate undo entry (MAP-1547).
    if (!this.focusedNode.isNew) {
      const session = this.focusSessionService.getCurrentSession();
      if (
        session &&
        session.entityId === this.focusedNode.id &&
        session.entityType === 'node' &&
        session.field === 'name' &&
        name !== session.startValue
      ) {
        const textCommand = this.commandFactory.createTextChangeCommand(
          this.focusedNode.id,
          'node',
          'name',
          session.startValue,
          name,
          session.id,
        );
        this.undoRedoManager.pushCommand(textCommand);
      }
    }

    // Direct debounced save — bypasses the form pipeline which breaks after Enter/newline
    clearTimeout(this.nameSaveTimer);
    this.nameSaveTimer = setTimeout(() => {
      if (this.focusedNode?.id && name !== this.lastSavedSnapshot?.['name']) {
        this.updateNodeEvent.emit({id: this.focusedNode.id, name} as any);
        this.lastSavedSnapshot = {...this.lastSavedSnapshot, name};
      }
    }, 800);
  }

  onNameDescriptionChange(event: { title: string; description?: string }) {
    this.formGroup.patchValue({
      name: event.title,
      description: event.description,
    });
  }

  onNameFieldBlur() {
    if (this.focusedNode && this.focusedNode.id && this.cytoscapeService.cy) {
      this.focusedNode.isNew = false;
      const cyElement = this.cytoscapeService.cy.getElementById(
        this.focusedNode.id,
      );
      if (cyElement.length > 0) {
        cyElement.removeData('isNew');
      }
    }
    // Freeze the name-change entry: further edits (e.g. after reselecting)
    // start a new focus session and land on their own undo entry.
    this.focusSessionService.endSession(this.formGroup.get('name')?.value ?? '');
  }

  onDescriptionFieldFocus() {
    if (!this.focusedNode?.id) return;
    const currentValue = this.cytoscapeService.cy
      ?.getElementById(this.focusedNode.id)
      ?.data('description') ?? this.focusedNode.description ?? '';
    this.focusSessionService.startSession(this.focusedNode.id, 'node', 'description', currentValue);
  }

  onDescriptionFieldBlur() {
    this.focusSessionService.endSession(this.formGroup.get('description')?.value ?? '');
  }

  private checkForUnsavedChanges() {
    if (this.isPatchingForm || !this.focusedNode) return;
    const raw = this.formGroup.getRawValue();
    const formValues = {
      ...raw,
      properties: {...raw.properties, scale: parseFloat(raw.properties?.scale as string)}
    };
    const toCompare = pick(this.lastSavedSnapshot, Object.keys(formValues));
    const c = diff(omit(toCompare, 'excludedFromState'), omit(formValues, 'excludedFromState'));
    if (!isEmpty(c)) {
      this.update();
    }
  }

  update() {
    if (!this.focusedNode) return;
    const raw = this.formGroup.getRawValue();
    const metadataFormData =
      this.formGroup.get('metadata')?.getRawValue()?.pairs ||
      this.formGroup.get('metadata')?.getRawValue();
    // @ts-ignore
    const node: Partial<ExtendedNode> = {
      id: this.focusedNode?.id || '',
      name: raw.name as string,
      description: raw.description as string,
      color: raw.color as string,
      type: raw.type as string,
      imageUrl: raw.imageUrl as string,
      // Ensure weight is correctly parsed and not accidentally divided by displayMultiplier
      weight: parseFloat(raw.weight as string),
      excludedFromState: raw.excludedFromState === 'true',
      properties: {
        imageOpacity: this.getValueOrDefault(
          raw.properties.imageOpacity,
          this.DefaultValues.node.properties.imageOpacity,
        ),
        ...this.imageScaleProperty(raw.properties.imageScale),
        opacity: this.getValueOrDefault(
          raw.properties.opacity,
          this.DefaultValues.node.properties.opacity,
        ),
        colorOpacity: this.getValueOrDefault(
          raw.properties.colorOpacity,
          this.DefaultValues.node.properties.colorOpacity,
        ),
        textValign: raw.properties.textValign || '',
        textHalign: raw.properties.textHalign || '',
        label: this.getValueOrDefault(
          raw.properties.label,
          this.DefaultValues.node.properties.label,
        ),
        textRotation: this.getValueOrDefault(
            raw.properties.textRotation,
            this.DefaultValues.node.properties.textRotation,
        ),
        textMarginX: this.getValueOrDefault(
          raw.properties.textMarginX,
          this.DefaultValues.node.properties.textMarginX,
        ),
        textMarginY: this.getValueOrDefault(
          raw.properties.textMarginY,
          this.DefaultValues.node.properties.textMarginY,
        ),
        textFontSize: raw.properties.textFontSize || '0',
        textFontColor: raw.properties.textFontColor || '',
        scale: this.getScale(raw.properties.scale),
        borderStyle: this.getValueOrDefault(
          raw.properties.borderStyle,
          this.DefaultValues.node.properties.borderStyle,
        ),
        soundUrl:
          raw.properties.soundUrl ||
          this.DefaultValues.node.properties.soundUrl,
      },
      // @ts-ignore
      originalNode: this.focusedNode.originalNode,
      states: this.focusedNode.states,
      metadata: metadataFormData.map((obj: any) => {
        if (obj.id === '') {
          const { id, ...rest } = obj;
          return rest;
        } else {
          return obj;
        }
      }).filter((obj: any) => (obj.key && obj.key.trim() !== '') || (obj.value && obj.value.trim() !== ''))
        // system metadata never passes through the form control — carry it over
        .concat(systemMetadata(this.focusedNode.metadata)),
    };
    this.focusedNode = { ...this.focusedNode, ...node };
    this.focusedNode.metadata = orderBy(
      this.focusedNode.metadata ? [...this.focusedNode.metadata] : [],
      ['order'],
      ['asc'],
    );
    node.metadata = this.focusedNode.metadata;

    // Update snapshot to reflect the saved state
    this.lastSavedSnapshot = safeDeepClone(this.focusedNode);
    this.lastSavedSnapshot['metadata'] = orderBy(visibleMetadata(this.lastSavedSnapshot['metadata']), ['order'], ['asc']);

    this.updateNodeEvent.emit(node);
  }

  get hasBottleneckMark(): boolean {
    return !!this.focusedNode?.metadata?.some(m => m.key === BOTTLENECK_KEY);
  }

  get visibleMetaCount(): number {
    return visibleMetadata(this.focusedNode?.metadata).length;
  }

  onBottleneckChange(metadata: MetaData[]): void {
    if (!this.focusedNode?.id) return;
    this.focusedNode = {...this.focusedNode, metadata};
    this.lastSavedSnapshot = safeDeepClone(this.focusedNode);
    this.lastSavedSnapshot['metadata'] = orderBy(visibleMetadata(this.lastSavedSnapshot['metadata']), ['order'], ['asc']);
    this.updateNodeEvent.emit({id: this.focusedNode.id, metadata} as Partial<ExtendedNode>);
  }

  private getScale(scaleValue: any): number {
    // Explicitly handle scale of 0 to make sure it's preserved
    if (scaleValue === 0 || scaleValue === '0') {
      return 0;
    }
    return scaleValue || DefaultValues.node.properties.scale;
  }

  private getValueOrDefault(value: any, defaultValue: any): any {
    return value !== undefined && value !== null ? value : defaultValue;
  }

  private imageScaleProperty(value: any): { imageScale?: number } {
    const stored = this.focusedNode?.properties?.imageScale;
    const scale = resolveImageScale(value);
    if ((stored === undefined || stored === null) && scale === DefaultValues.node.properties.imageScale) {
      return {};
    }
    return { imageScale: scale };
  }
  protected readonly metadata = metadata;
  protected readonly PACKAGE_NAMES = PACKAGE_NAMES;

  constructor(
    private cdr: ChangeDetectorRef,
    private mapService: MapService,
    private cytoscapeService: CytoscapeService,
    private packageService: PackageService,
    private el: ElementRef,
    private focusSessionService: FocusSessionService,
    private undoRedoManager: UndoRedoManagerService,
    private commandFactory: CommandFactoryService,
  ) {
    super();
    this.currentMap = this.mapService.getCurrentSelectedMapFromStore();
  }

  get isDefaulter(): boolean {
    return this.packageService.isDefaulter();
  }
}

interface ExtendedNode extends GraphNode {
  isNew?: boolean;
}
